// FirstVisitInstallPrompt — a one-time popup nudging the first-time visitor
// to install the desktop / home-screen app.
//
// Shows when ALL of:
//   • the browser has fired `beforeinstallprompt` (i.e. installable),
//   • the app isn't already running standalone,
//   • the user hasn't dismissed or accepted before
//     (we remember that in localStorage under MYHOTEL_INSTALL_DISMISSED).
//
// Designed to fit the brown/gold theme of the rest of the product.  Pure
// React + inline styles — no MUI dependency so it works on every page.

import React, { useEffect, useState } from "react";
import { usePWAInstall } from "../hooks/usePWAInstall";

const STORAGE_KEY = "MYHOTEL_INSTALL_DISMISSED";

export default function FirstVisitInstallPrompt() {
  const { canInstall, isInstalled, promptInstall } = usePWAInstall();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Decide once we know the install state — wait until the deferred prompt
  // event has fired so the popup never lies about being able to install.
  useEffect(() => {
    if (isInstalled) return;
    if (!canInstall) return;
    if (typeof window === "undefined") return;
    const dismissed = window.localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    // Tiny delay so the popup eases in instead of slamming on first paint.
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [canInstall, isInstalled]);

  const dismiss = (remember = true) => {
    if (remember) {
      try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
    }
    setOpen(false);
  };

  const install = async () => {
    setBusy(true);
    try {
      const outcome = await promptInstall();
      // Whatever they chose, don't keep asking on every load.
      try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
      setOpen(false);
      return outcome;
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <style>{`
        .pwa-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 5500;
          background: radial-gradient(ellipse at center, rgba(20,10,5,.65) 0%, rgba(15,8,3,.85) 100%);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          display: grid;
          place-items: center;
          animation: pwa-overlay-in .25s ease-out both;
          padding: 20px;
        }
        .pwa-modal-card {
          width: min(440px, 100%);
          background: #fdf8f2;
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(180,140,80,.25);
          box-shadow: 0 24px 80px rgba(20,10,5,.45), 0 0 0 1px rgba(200,160,90,.15);
          font-family: 'Jost', system-ui, sans-serif;
          animation: pwa-card-in .35s cubic-bezier(.22,1,.36,1) both;
        }
        .pwa-modal-hero {
          background: linear-gradient(135deg, #2c1f14 0%, #3d2a1a 100%);
          padding: 30px 28px 24px;
          color: #f0e4cf;
          position: relative;
          overflow: hidden;
          text-align: center;
        }
        .pwa-modal-hero::before {
          content: '';
          position: absolute;
          inset: -50% -10% auto auto;
          width: 220px;
          height: 220px;
          background: radial-gradient(circle, rgba(200,160,90,.22) 0%, transparent 70%);
          pointer-events: none;
        }
        .pwa-modal-logo {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          margin: 0 auto 16px;
          display: grid;
          place-items: center;
          background: rgba(200,160,90,.18);
          border: 1.5px solid rgba(200,160,90,.55);
          position: relative;
          z-index: 1;
        }
        .pwa-modal-logo img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
        .pwa-modal-eyebrow {
          font-size: 10.5px;
          font-weight: 500;
          letter-spacing: .22em;
          text-transform: uppercase;
          color: #c8a05a;
          margin-bottom: 8px;
          position: relative; z-index: 1;
        }
        .pwa-modal-title {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 300;
          font-size: 28px;
          line-height: 1.15;
          position: relative; z-index: 1;
        }
        .pwa-modal-title em { font-style: italic; color: #c8a05a; }
        .pwa-modal-body { padding: 26px 28px 24px; }
        .pwa-modal-desc {
          font-size: 14px;
          font-weight: 300;
          color: #4a3828;
          line-height: 1.6;
          margin-bottom: 18px;
          text-align: center;
        }
        .pwa-modal-bullets {
          display: grid;
          gap: 10px;
          margin-bottom: 22px;
        }
        .pwa-modal-bullet {
          display: flex;
          gap: 11px;
          align-items: flex-start;
          font-size: 13px;
          color: #3a2a1a;
          font-weight: 300;
        }
        .pwa-modal-bullet-icon {
          flex-shrink: 0;
          width: 26px;
          height: 26px;
          border-radius: 7px;
          display: grid;
          place-items: center;
          background: rgba(200,160,90,.13);
          color: #a8814a;
        }
        .pwa-modal-actions { display: flex; gap: 10px; }
        .pwa-modal-btn {
          flex: 1;
          padding: 13px 14px;
          border-radius: 9px;
          font-family: 'Jost', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: .18em;
          text-transform: uppercase;
          cursor: pointer;
          border: none;
          transition: transform .15s ease, background .2s, filter .2s;
        }
        .pwa-modal-btn-primary {
          background: linear-gradient(135deg, #2c1f14 0%, #3d2a1a 100%);
          color: #f0e4cf;
          box-shadow: 0 4px 16px rgba(40,20,5,.22), inset 0 1px 0 rgba(200,160,90,.18);
        }
        .pwa-modal-btn-primary:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.08); }
        .pwa-modal-btn-primary:disabled { opacity: .65; cursor: progress; }
        .pwa-modal-btn-ghost {
          background: transparent;
          color: #6b5a4e;
          border: 1.5px solid rgba(44,31,20,.22);
        }
        .pwa-modal-btn-ghost:hover { color: #2c1f14; border-color: #2c1f14; }
        .pwa-modal-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: rgba(255,255,255,.06);
          color: rgba(240,228,207,.7);
          border: 1px solid rgba(200,160,90,.25);
          cursor: pointer;
          display: grid;
          place-items: center;
          z-index: 2;
          transition: background .2s, color .2s;
        }
        .pwa-modal-close:hover { background: rgba(255,255,255,.12); color: #f0e4cf; }
        @keyframes pwa-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pwa-card-in {
          from { opacity: 0; transform: translateY(20px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div className="pwa-modal-overlay" onClick={() => dismiss()}>
        <div className="pwa-modal-card" onClick={(e) => e.stopPropagation()}>
          <div className="pwa-modal-hero">
            <button
              type="button"
              className="pwa-modal-close"
              onClick={() => dismiss()}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="pwa-modal-logo">
              <img src="/app-icon.jpeg" alt="MyHotel" />
            </div>
            <div className="pwa-modal-eyebrow">Quick Launch</div>
            <h2 className="pwa-modal-title">
              Install <em>MyHotel</em>
            </h2>
          </div>

          <div className="pwa-modal-body">
            <p className="pwa-modal-desc">
              Add MyHotel to this device so you can launch it instantly —
              just like a desktop app, with its own icon.
            </p>

            <div className="pwa-modal-bullets">
              <div className="pwa-modal-bullet">
                <span className="pwa-modal-bullet-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="3" ry="3" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </span>
                Launch from the desktop or home screen.
              </div>
              <div className="pwa-modal-bullet">
                <span className="pwa-modal-bullet-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="9" />
                    <polyline points="12 7 12 12 15 14" />
                  </svg>
                </span>
                Opens in seconds — no browser tab needed.
              </div>
              <div className="pwa-modal-bullet">
                <span className="pwa-modal-bullet-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77 5.82 21.02 7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </span>
                A polished native-app feel for your operators.
              </div>
            </div>

            <div className="pwa-modal-actions">
              <button
                type="button"
                className="pwa-modal-btn pwa-modal-btn-ghost"
                onClick={() => dismiss()}
              >
                Not now
              </button>
              <button
                type="button"
                className="pwa-modal-btn pwa-modal-btn-primary"
                onClick={install}
                disabled={busy}
              >
                {busy ? "Installing…" : "Install"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
